import React from 'react';
import { DatePickerProps, DateRange } from '@/shared/types/datepicker';
import { useDatePicker, useDateRange } from './hooks';
import { DateInput, DateTimeReadOnlyInput } from './molecules';
import {
  formatDisplayDateTime,
  formatDisplayDateTimeRange,
  mergeDateWithTimeParts,
  dateToParts,
  DEFAULT_TIME_PARTS,
} from './utils/datetimeFormat';
import {
  getActiveRangeEndpoint,
  mergeCalendarDayWithRangeTime,
  resolveDatePickerTimeConfig,
} from './utils/resolveTimeConfig';
import { DatePickerPortal } from './organisms';
import { cn } from '@/shared/utils/cn';

/**
 * Selector de fecha única o rango con popup de calendario y soporte opcional de hora.
 *
 * @example
 * ```tsx
 * <DatePicker
 *   mode="range"
 *   time={{ showTime: true, enabled: true, currentTime: true }}
 *   selectedRange={range}
 *   onRangeChange={setRange}
 * />
 * ```
 */
const DatePicker: React.FC<DatePickerProps> = (props) => {
  const {
    mode = 'single',
    onDateChange,
    onRangeChange,
    minDate,
    maxDate,
    locale = 'es-ES',
    weekDays = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
    monthNames = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ],
    label = '',
    startOfWeek = 1,
    className = '',
    inputClassName = '',
    showWeekNumbers = false,
    yearRange = 12,
    closeOnSelect = true,
    autoCloseRange = true,
    placeholder = 'Seleccionar fecha',
    format = 'dd/MM/yyyy',
    calendarsCount = 1,
    disabled = false,
    renderHeader,
    renderDay,
    size = 'sm',
  } = props;

  const timeConfig = resolveDatePickerTimeConfig(props);
  const { visible: timeVisible, enabled: timeEditable, currentTime, showSeconds } = timeConfig;

  const {
    state,
    selectedDate,
    selectedRange,
    selectedDateForInput,
    selectedRangeForInput,
    inputRef,
    portalHostRef,
    isPortalReady,
    effectiveAutoCloseRange,
    actions,
  } = useDatePicker(props);

  const { handleRangeSelection } = useDateRange(
    selectedRange,
    actions.setSelectedRange,
    minDate,
    maxDate,
    effectiveAutoCloseRange
  );

  const formatDateForInput = (date: Date | null) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return format.replace('dd', day).replace('MM', month).replace('yyyy', year.toString());
  };

  const formatRangeForInput = (range: { start: Date | null; end: Date | null }) => {
    if (!range || (!range.start && !range.end)) return '';

    if (timeVisible) {
      return formatDisplayDateTimeRange(range, { showSeconds });
    }

    const startIsValid =
      range.start && range.start instanceof Date && !isNaN(range.start.getTime());
    const endIsValid = range.end && range.end instanceof Date && !isNaN(range.end.getTime());

    if (startIsValid && endIsValid) {
      return `${formatDateForInput(range.start)} - ${formatDateForInput(range.end)}`;
    }

    if (startIsValid) {
      return formatDateForInput(range.start);
    }

    if (endIsValid) {
      return formatDateForInput(range.end);
    }

    return '';
  };

  const handleDateSelect = (date: Date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('DatePicker: Se recibió una fecha inválida:', date);
      }
      return;
    }

    let resolvedDate = date;

    if (timeVisible && mode === 'single') {
      const baseForTime = state.pendingDate ?? selectedDateForInput;
      const timeParts = baseForTime
        ? dateToParts(baseForTime, showSeconds)
        : currentTime
          ? dateToParts(new Date(), showSeconds)
          : DEFAULT_TIME_PARTS;
      resolvedDate = mergeDateWithTimeParts(date, timeParts, showSeconds);
    } else if (timeVisible && mode === 'range') {
      resolvedDate = mergeCalendarDayWithRangeTime(
        date,
        selectedRange,
        state.pendingRange,
        currentTime,
        showSeconds
      );
    }

    if (mode === 'single') {
      actions.setSelectedDate(resolvedDate);
    } else {
      handleRangeSelection(resolvedDate, state.hoverDate);
    }
  };

  const handlePopupTimeChange = (date: Date | null) => {
    if (!date || !timeEditable) return;

    if (mode === 'single') {
      actions.setSelectedDate(date, false);
      return;
    }

    if (mode === 'range' && timeVisible) {
      const endpoint = getActiveRangeEndpoint(selectedRange);
      const nextRange: DateRange =
        endpoint === 'end'
          ? { start: selectedRange.start, end: date }
          : { start: date, end: selectedRange.end ?? null };
      actions.setSelectedRange(nextRange, false);
    }
  };

  const effectiveCloseOnSelect = timeVisible ? false : closeOnSelect;
  const requiresExplicitConfirm =
    timeVisible || !effectiveCloseOnSelect || !effectiveAutoCloseRange;

  const handleDateHover = (date: Date | null) => {
    if (date !== null && (!(date instanceof Date) || isNaN(date.getTime()))) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('DatePicker: Se recibió una fecha inválida en hover:', date);
      }
      return;
    }

    actions.setHoverDate(date);
  };

  const handleAccept = () => {
    if (mode === 'single') {
      if (requiresExplicitConfirm && state.pendingDate !== null) {
        actions.setSelectedDate(state.pendingDate, true);
        onDateChange?.(state.pendingDate);
      }
    } else if (mode === 'range') {
      if (requiresExplicitConfirm && (state.pendingRange.start || state.pendingRange.end)) {
        actions.setSelectedRange(state.pendingRange, true);
        onRangeChange?.(state.pendingRange);
      }
    }
    actions.closePicker();
  };

  const handleCancel = () => {
    if (mode === 'single' && requiresExplicitConfirm) {
      actions.setPendingDate(null);
      actions.restoreOriginalDate();
    } else if (mode === 'range' && requiresExplicitConfirm) {
      actions.setPendingRange({ start: null, end: null });
      actions.restoreOriginalRange();
    }
    actions.closePicker();
  };

  const getInputValue = () => {
    if (mode === 'single') {
      if (timeVisible) {
        return formatDisplayDateTime(selectedDateForInput, { showSeconds });
      }
      return formatDateForInput(selectedDateForInput);
    }
    return formatRangeForInput(selectedRangeForInput);
  };

  const datetimePlaceholder =
    placeholder === 'Seleccionar fecha' ? 'Selecciona fecha y hora' : placeholder;

  const rangeDatetimeDisplay =
    timeVisible && mode === 'range' ? formatRangeForInput(selectedRangeForInput) : undefined;

  return (
    <div className={cn('ui-imj', 'relative', className)}>
      {timeVisible && mode === 'single' ? (
        <DateTimeReadOnlyInput
          ref={inputRef}
          value={selectedDateForInput}
          placeholder={datetimePlaceholder}
          label={label}
          size={size}
          isOpen={state.isOpen}
          onInputClick={actions.openPicker}
          className={inputClassName}
          disabled={disabled}
          showSeconds={showSeconds}
        />
      ) : timeVisible && mode === 'range' ? (
        <DateTimeReadOnlyInput
          ref={inputRef}
          value={selectedRangeForInput.start}
          displayValue={rangeDatetimeDisplay}
          placeholder={datetimePlaceholder}
          label={label}
          size={size}
          isOpen={state.isOpen}
          onInputClick={actions.openPicker}
          className={inputClassName}
          disabled={disabled}
          showSeconds={showSeconds}
        />
      ) : (
        <DateInput
          ref={inputRef}
          value={getInputValue()}
          placeholder={placeholder}
          label={label}
          size={size}
          isOpen={state.isOpen}
          onInputClick={actions.openPicker}
          className={inputClassName}
          format={format}
          disabled={disabled}
        />
      )}

      <DatePickerPortal
        isOpen={!disabled && state.isOpen && isPortalReady}
        onClose={actions.closePicker}
        portalPosition={state.portalPosition}
        portalHostRef={portalHostRef}
        currentDate={state.currentDate}
        selectedDate={selectedDate}
        selectedRange={selectedRange}
        onDateSelect={handleDateSelect}
        onDateHover={handleDateHover}
        onViewModeChange={actions.setViewMode}
        onNavigate={actions.navigate}
        onMonthSelect={actions.handleMonthSelect}
        onYearSelect={actions.handleYearSelect}
        onGoToToday={actions.goToToday}
        viewMode={state.viewMode}
        minDate={minDate}
        maxDate={maxDate}
        locale={locale}
        weekDays={weekDays}
        monthNames={monthNames}
        startOfWeek={startOfWeek}
        showWeekNumbers={showWeekNumbers}
        yearRange={yearRange}
        hoverDate={state.hoverDate}
        closeOnSelect={effectiveCloseOnSelect}
        autoCloseRange={effectiveAutoCloseRange}
        calendarsCount={calendarsCount}
        mode={mode}
        showTime={timeVisible}
        timeEditable={timeEditable}
        showSeconds={showSeconds}
        onTimeChange={handlePopupTimeChange}
        onAccept={handleAccept}
        onCancel={handleCancel}
        renderHeader={renderHeader}
        renderDay={renderDay}
      />
    </div>
  );
};

export { DatePicker };
